import React, { useCallback, useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';

import { ImageInput } from '@/components/molecules/ImageInput';
import { SvyIcon } from '@/components/savory/SvyIcon';

import { BANNER_MAX_FILE_SIZE, validateBannerImage } from '../../utils/validateBannerImage';
import {
    ImageSection,
    ImageDescriptionBar,
    ImageDescriptionText,
    ReplaceButton,
    ImageSpecs,
} from '../../BannerModal.style';

type ImageOwner = { brandId: number } | { restaurantId: number };

type Props = {
    imageId: number | null;
    defaultImage?: { fullUrl: string; imageId: number };
    imageSpecsMessageId: string;
    imageDescriptionMessageId: string;
    replaceMessageId: string;
    errorRatioMessageId: string;
    onChange: (imageId: number | null) => void;
} & ImageOwner;

export const BannerImageSection: React.FC<Props> = ({
    imageId,
    defaultImage,
    imageSpecsMessageId,
    imageDescriptionMessageId,
    replaceMessageId,
    errorRatioMessageId,
    onChange,
    ...ownerProps
}) => {
    const imageSectionRef = useRef<HTMLDivElement>(null);

    const handleReplace = useCallback(() => {
        const editBtn = imageSectionRef.current?.querySelector<HTMLButtonElement>(
            '[data-testid="imageInput-open"]:last-of-type',
        );
        editBtn?.click();
    }, []);

    const validateFile = useCallback(
        async (file: File): Promise<string | null> => {
            const result = await validateBannerImage(file);
            if (!result.ok && result.reason === 'ratio') {
                return errorRatioMessageId;
            }
            return null;
        },
        [errorRatioMessageId],
    );

    const imageFileName = useMemo(() => {
        if (!defaultImage?.fullUrl) return null;
        try {
            const url = new URL(defaultImage.fullUrl);
            return decodeURIComponent(url.pathname.split('/').pop() ?? '');
        } catch {
            return null;
        }
    }, [defaultImage?.fullUrl]);

    return (
        <>
            <ImageSection ref={imageSectionRef}>
                <ImageInput
                    {...ownerProps}
                    value={imageId ?? undefined}
                    defaultImage={defaultImage}
                    maxFileSize={BANNER_MAX_FILE_SIZE}
                    validateFile={validateFile}
                    disableResize
                    onChange={onChange}
                />
                {imageId && (
                    <ImageDescriptionBar>
                        <ImageDescriptionText>
                            <FormattedMessage
                                id={imageDescriptionMessageId}
                                values={{ name: imageFileName ?? `image-${imageId}` }}
                            />
                        </ImageDescriptionText>
                        <ReplaceButton type="button" onClick={handleReplace}>
                            <SvyIcon icon="pencil-line" />
                            <FormattedMessage id={replaceMessageId} />
                        </ReplaceButton>
                    </ImageDescriptionBar>
                )}
            </ImageSection>
            {!imageId && (
                <ImageSpecs>
                    <FormattedMessage id={imageSpecsMessageId} />
                </ImageSpecs>
            )}
        </>
    );
};
